import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { mealsAPI } from '../../services/meals.api';
import { selectUser } from '../../store/slices/authSlice';

// Material UI imports
import {
  Typography,
  Box,
  Container,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Chip,
  Paper,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  useTheme,
  alpha,
  Fade,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery
} from '@mui/material';

// Icons
import {
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  RestaurantMenu,
  Restaurant,
  AccessTime,
  Create as EditIcon,
  Delete as DeleteIcon,
  LocalDining,
  Fastfood,
  Add as AddIcon,
  FolderOpen,
  Sort as SortIcon,
  FilterList,
  MoreVert,
  SmartToy,
  WbSunny
} from '@mui/icons-material';

const Favorites = () => {
  const [favorites, setFavorites] = useState([]);
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolderDialog, setShowNewFolderDialog] = useState(false);
  const [actionMessage, setActionMessage] = useState('');
  const [highlightedMeal, setHighlightedMeal] = useState(null);
  
  const theme = useTheme();
  const user = useSelector(selectUser);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  
  useEffect(() => {
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    try {
      setLoading(true);
      // Get data from API - no fallback to ensure database storage
      const response = await mealsAPI.getFavorites();
      
      // Filter out unnamed meals or incomplete entries
      const favoritesData = response.data.filter(fav => {
        // Only keep favorites that have either a valid meal object with a name
        // or a direct mealName property that isn't empty
        return (fav.meal && fav.meal.name) || (fav.mealName && fav.mealName !== 'Unnamed Meal');
      });
      
      // Extract unique folders
      const uniqueFolders = [...new Set(favoritesData.map(fav => fav.folder))];
      setFolders(['all', ...uniqueFolders]);
      setFavorites(favoritesData);
    } catch (error) {
      console.error('Error fetching favorites:', error);
      setError('Failed to load favorites. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFavorite = async (mealId) => {
    try {
      // Use the real API to remove from database
      await mealsAPI.removeFromFavorites(mealId);
      // Update UI after successful API call
      setFavorites(favorites.filter(fav => 
        fav.meal?._id !== mealId && fav._id !== mealId
      ));
      setActionMessage('Meal removed from favorites');
    } catch (error) {
      console.error('Error removing favorite:', error);
      setActionMessage('Failed to remove from favorites. Please try again.');
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      setActionMessage('Please enter a folder name');
      return;
    }
    
    // Add the new folder to the state
    setFolders([...folders, newFolderName]);
    setNewFolderName('');
    setShowNewFolderDialog(false);
    
    // Make the new folder active
    setActiveFolder(newFolderName);
    setActionMessage(`Folder '${newFolderName}' created`);
  };

  const handleMoveToFolder = async (favoriteId, newFolder) => {
    try {
      // Make API call to update folder
      await mealsAPI.updateFavoriteFolder(favoriteId, newFolder);
      
      // Update local state
      setFavorites(favorites.map(fav => 
        fav._id === favoriteId ? { ...fav, folder: newFolder } : fav
      ));
      setActionMessage(`Meal moved to '${newFolder}' folder`);
    } catch (error) {
      console.error('Error moving favorite to folder:', error);
      setActionMessage('Failed to move meal to folder. Please try again.');
    }
  };

  const filteredFavorites = activeFolder === 'all' 
    ? favorites 
    : favorites.filter(fav => fav.folder === activeFolder);
    
  // Function to get the dietary icon
  const getDietaryIcon = (type) => {
    const lowerType = typeof type === 'string' ? type.toLowerCase() : '';
    if (lowerType.includes('veg') && !lowerType.includes('non')) {
      return <LocalDining fontSize="small" sx={{ mr: 0.5 }} />;
    } else if (lowerType.includes('non') || lowerType.includes('meat')) {
      return <Fastfood fontSize="small" sx={{ mr: 0.5 }} />;
    } else {
      return <Restaurant fontSize="small" sx={{ mr: 0.5 }} />;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Page Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box display="flex" alignItems="center">
          <FavoriteIcon fontSize="large" color="error" sx={{ mr: 1.5 }} />
          <Typography variant="h4" fontWeight="bold">
            My Meal Vault
          </Typography>
        </Box>
        
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setShowNewFolderDialog(true)}
        >
          New Folder
        </Button>
      </Box>
      
      {/* Success/Error Message */}
      {actionMessage && (
        <Fade in={Boolean(actionMessage)}>
          <Alert 
            severity={actionMessage.includes('Failed') ? "error" : "success"} 
            sx={{ mb: 3, boxShadow: 2 }}
            onClose={() => setActionMessage('')}
          >
            {actionMessage}
          </Alert>
        </Fade>
      )}
      
      {/* Folder Tabs */}
      <Paper sx={{ mb: 4, p: 1, borderRadius: 2, overflowX: 'auto' }}>
        <Box display="flex" flexWrap="nowrap" gap={1} sx={{ minWidth: 'min-content' }}>
          {folders.map(folder => (
            <Chip
              key={folder}
              label={folder === 'default' ? 'General' : folder}
              icon={<FolderOpen />}
              onClick={() => setActiveFolder(folder)}
              color={activeFolder === folder ? "primary" : "default"}
              variant={activeFolder === folder ? "filled" : "outlined"}
              sx={{ px: 1, fontWeight: activeFolder === folder ? 'bold' : 'normal' }}
            />
          ))}
        </Box>
      </Paper>
      
      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
          <CircularProgress color="primary" size={60} />
        </Box>
      )}
      
      {/* Error State */}
      {error && !loading && (
        <Alert severity="error" sx={{ my: 4 }}>
          {error}
        </Alert>
      )}
      
      {/* Meal Cards Grid */}
      {!loading && !error && (
        <>
          <Grid container spacing={3}>
            {filteredFavorites.length > 0 ? filteredFavorites.map((favorite) => (
              <Grid item xs={12} sm={6} md={4} key={favorite._id}>
                <Card 
                  elevation={4} 
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    borderRadius: 2,
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: 8,
                    },
                    ...(highlightedMeal === favorite._id && {
                      outline: `2px solid ${theme.palette.primary.main}`,
                      boxShadow: 8,
                    }),
                  }}
                  onMouseEnter={() => setHighlightedMeal(favorite._id)}
                  onMouseLeave={() => setHighlightedMeal(null)}
                >
                  {/* Favorite and Source Badge */}
                  <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 2, display: 'flex', gap: 1 }}>
                    {/* AI Source Indicator */}
                    {favorite.meal?.aiGenerated || favorite.meal?.tags?.includes('ai-generated') || favorite.folder === 'AI Suggestions' ? (
                      <Tooltip title="AI-Generated Suggestion" arrow>
                        <Paper elevation={3} sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          borderRadius: '12px', 
                          px: 1, 
                          py: 0.5, 
                          backgroundColor: alpha(theme.palette.secondary.main, 0.8)
                        }}>
                          <SmartToy sx={{ color: 'white', fontSize: 16 }} />
                        </Paper>
                      </Tooltip>
                    ) : null}
                    
                    {/* Favorite Button */}
                    <Tooltip title="Remove from favorites" arrow>
                      <IconButton
                        onClick={() => favorite.meal ? handleRemoveFavorite(favorite.meal._id) : handleRemoveFavorite(favorite._id)}
                        sx={{ 
                          bgcolor: alpha(theme.palette.error.main, 0.8),
                          color: 'white',
                          '&:hover': { bgcolor: theme.palette.error.main }
                        }}
                        size="small"
                      >
                        <FavoriteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  
                  {/* Meal Image */}
                  <Box sx={{ position: 'relative', paddingTop: '56.25%' /* 16:9 Aspect Ratio */ }}>
                    <CardMedia
                      component="img"
                      image={favorite.mealImage || favorite.meal?.imageUrl || `https://source.unsplash.com/random/400x300/?food,${favorite.mealName || (favorite.meal?.name?.replace(' ', ',') || 'meal')}`}
                      alt={favorite.mealName || favorite.meal?.name || 'Favorite Meal'}
                      onError={(e) => {
                        // If image fails to load, replace with a fallback
                        e.target.onerror = null;
                        e.target.src = `https://source.unsplash.com/random/400x300/?food,meal`;
                      }}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  </Box>
                  
                  <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {favorite.mealName || (favorite.meal ? favorite.meal.name : 'Unnamed Meal')}
                      </Typography>
                    </Box>
                    
                    {(favorite.mealDescription || favorite.meal?.description) && (
                      <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 1 }}>
                        {favorite.mealDescription || favorite.meal?.description}
                      </Typography>
                    )}
                    
                    <Box display="flex" flexWrap="wrap" gap={0.5} mb={1}>
                      {/* Meal Type */}
                      {favorite.meal?.mealType && (
                        <Chip
                          size="small"
                          icon={favorite.meal.mealType === 'breakfast' ? <AccessTime fontSize="small" /> : 
                                favorite.meal.mealType === 'lunch' ? <RestaurantMenu fontSize="small" /> : 
                                <Restaurant fontSize="small" />}
                          label={favorite.meal.mealType}
                          color="default"
                          variant="filled"
                          sx={{ borderRadius: '16px', textTransform: 'capitalize', bgcolor: alpha(theme.palette.primary.main, 0.1) }}
                        />
                      )}
                      
                      {/* Dietary Type */}
                      {favorite.meal?.dietaryType && Array.isArray(favorite.meal.dietaryType) && favorite.meal.dietaryType.map((type) => (
                        <Chip
                          key={type}
                          size="small"
                          icon={getDietaryIcon(type)}
                          label={type}
                          color="primary"
                          variant="outlined"
                          sx={{ borderRadius: '16px', textTransform: 'capitalize' }}
                        />
                      ))}
                      
                      {/* Cuisine Style */}
                      {favorite.meal?.cuisineStyle && Array.isArray(favorite.meal.cuisineStyle) && favorite.meal.cuisineStyle.map((style) => (
                        <Chip
                          key={style}
                          size="small"
                          icon={<Restaurant fontSize="small" />}
                          label={style}
                          color="secondary"
                          variant="outlined"
                          sx={{ borderRadius: '16px', textTransform: 'capitalize' }}
                        />
                      ))}
                      
                      {/* Seasonality */}
                      {favorite.meal?.seasonality && Array.isArray(favorite.meal.seasonality) && 
                       favorite.meal.seasonality.filter(season => season !== 'all').map((season) => (
                        <Chip
                          key={season}
                          size="small"
                          icon={<WbSunny fontSize="small" />}
                          label={season}
                          color="info"
                          variant="outlined"
                          sx={{ borderRadius: '16px', textTransform: 'capitalize' }}
                        />
                      ))}
                    </Box>
                    
                    {/* AI-specific details when available */}
                    {(favorite.meal?.aiScore || favorite.meal?.aiReasons) && (
                      <Paper variant="outlined" sx={{ p: 1, mt: 1, borderRadius: 1, bgcolor: alpha(theme.palette.secondary.light, 0.1), borderColor: alpha(theme.palette.secondary.main, 0.2) }}>
                        <Box display="flex" alignItems="flex-start">
                          <SmartToy color="secondary" sx={{ mr: 1, fontSize: 18, mt: 0.3 }} />
                          <Box>
                            {favorite.meal.aiScore && (
                              <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                AI Match Score: {typeof favorite.meal.aiScore === 'number' ? 
                                  `${Math.round(favorite.meal.aiScore * 100)}%` : favorite.meal.aiScore}
                              </Typography>
                            )}
                            {favorite.meal.aiReasons && Array.isArray(favorite.meal.aiReasons) && (
                              <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                                {favorite.meal.aiReasons.map((reason, i) => (
                                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                    <span style={{ marginRight: '4px', fontSize: '10px' }}>•</span>
                                    {reason}
                                  </Box>
                                ))}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Paper>
                    )}
                    
                    {/* Notes */}
                    {favorite.notes && (
                      <Paper variant="outlined" sx={{ p: 1, mt: 1, borderRadius: 1, bgcolor: alpha(theme.palette.primary.light, 0.05) }}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Notes:</strong> {favorite.notes}
                        </Typography>
                      </Paper>
                    )}
                  </CardContent>
                  
                  <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
                    <Box display="flex" justifyContent="space-between" width="100%">
                      <Box sx={{ flexGrow: 1, mr: 1 }}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          variant="outlined"
                          label="Folder"
                          value={favorite.folder || 'default'}
                          onChange={(e) => handleMoveToFolder(favorite._id, e.target.value)}
                          SelectProps={{
                            MenuProps: {
                              sx: { maxHeight: 300 }
                            }
                          }}
                        >
                          {folders
                            .filter(f => f !== 'all')
                            .map(folder => (
                              <MenuItem key={folder} value={folder}>
                                {folder === 'default' ? 'General' : folder}
                              </MenuItem>
                            ))
                          }
                        </TextField>
                      </Box>
                      
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => favorite.meal ? handleRemoveFavorite(favorite.meal._id) : handleRemoveFavorite(favorite._id)}
                        size="medium"
                      >
                        {isSmallScreen ? '' : 'Remove'}
                      </Button>
                    </Box>
                  </CardActions>
                </Card>
              </Grid>
            )) : (
              <Grid item xs={12}>
                <Paper elevation={2} sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                  <FavoriteBorderIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    No favorite meals in {activeFolder === 'all' ? 'your collection' : `the '${activeFolder}' folder`} yet.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Browse meal suggestions and click the heart icon to add favorites.
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </>
      )}
      
      {/* New Folder Dialog */}
      <Dialog open={showNewFolderDialog} onClose={() => setShowNewFolderDialog(false)}>
        <DialogTitle>Create New Folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Folder Name"
            fullWidth
            variant="outlined"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowNewFolderDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateFolder} variant="contained" color="primary">
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Favorites;
